import { useEffect, useMemo, useState } from "react";
import { EnablerCard } from "./components/EnablerCard";
import { EnablerFilter } from "./components/EnablerFilter";
import { FilterPills } from "./components/FilterPills";
import { ObjectiveCard } from "./components/ObjectiveCard";
import { ObjectiveFilter } from "./components/ObjectiveFilter";
import { SearchBox } from "./components/SearchBox";
import { ChatWidget } from "./chatbot/components/ChatWidget";
import {
  countUniqueResources,
  createResourceIndexes,
  DEFAULT_RESOURCE_CATEGORY,
  getEnablerGroups,
  getFormats,
  getObjectiveGroups,
  hasSearchTerms,
  RESOURCE_CATEGORIES
} from "./lib/resources";
import type { LearningCatalog, ResourceCategory } from "./types";

const DATA_URL = "/data/learning-materials.json";

const EMPTY_CATALOG: LearningCatalog = {
  version: 1,
  objectives: [],
  enablers: [],
  resources: []
};

type BrowseMode = "objectives" | "enablers";

export function App() {
  const [catalog, setCatalog] = useState<LearningCatalog>(EMPTY_CATALOG);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<ResourceCategory>(DEFAULT_RESOURCE_CATEGORY);
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [selectedObjective, setSelectedObjective] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<string | null>(null);
  const [selectedEnablerTheme, setSelectedEnablerTheme] = useState<string | null>(null);
  const [selectedEnabler, setSelectedEnabler] = useState<string | null>(null);
  const [browseMode, setBrowseMode] = useState<BrowseMode>("objectives");

  const isHomeRoute = window.location.pathname === "/";

  useEffect(() => {
    if (!isHomeRoute) {
      setIsLoading(false);
      return;
    }

    let ignore = false;

    async function loadMaterials() {
      try {
        const response = await fetch(DATA_URL);
        if (!response.ok) {
          throw new Error(`Failed to load ${DATA_URL}: ${response.status}`);
        }

        const parsed = (await response.json()) as LearningCatalog;

        if (!ignore) {
          setCatalog(parsed);
          setError(null);
        }
      } catch (caught) {
        if (!ignore) {
          setError(caught instanceof Error ? caught.message : "Failed to load the library.");
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    }

    loadMaterials();

    return () => {
      ignore = true;
    };
  }, [isHomeRoute]);

  useEffect(() => {
    if (selectedCategory !== DEFAULT_RESOURCE_CATEGORY) {
      setSelectedFormat(null);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (browseMode === "objectives") {
      setSelectedEnablerTheme(null);
      setSelectedEnabler(null);
    } else {
      setSelectedStage(null);
      setSelectedObjective(null);
    }
  }, [browseMode]);

  const formats = useMemo(() => getFormats(catalog, selectedCategory), [catalog, selectedCategory]);
  const indexes = useMemo(() => createResourceIndexes(catalog), [catalog]);
  const filters = useMemo(
    () => ({
      searchTerm,
      selectedCategory,
      selectedStage,
      selectedObjective,
      selectedFormat,
      selectedEnablerTheme,
      selectedEnabler
    }),
    [searchTerm, selectedCategory, selectedEnabler, selectedEnablerTheme, selectedFormat, selectedObjective, selectedStage]
  );
  const objectiveGroups = useMemo(() => getObjectiveGroups(catalog, filters), [catalog, filters]);
  const enablerGroups = useMemo(() => getEnablerGroups(catalog, filters), [catalog, filters]);
  const activeGroups = browseMode === "objectives" ? objectiveGroups : enablerGroups;
  const filteredResourceCount = useMemo(() => countUniqueResources(activeGroups), [activeGroups]);
  const hasActiveSearch = hasSearchTerms(searchTerm);
  const hasActiveFilters = Boolean(
    hasActiveSearch ||
      selectedCategory !== DEFAULT_RESOURCE_CATEGORY ||
      selectedStage ||
      selectedObjective ||
      selectedFormat ||
      selectedEnablerTheme ||
      selectedEnabler
  );
  const trimmedSearchTerm = searchTerm.trim();
  const shouldOpenMatches = hasActiveSearch && trimmedSearchTerm.length > 2;
  const handleSelectStage = (stage: string | null) => {
    setSelectedStage(stage);
    setSelectedObjective(null);
  };
  const handleSelectObjective = (objectiveId: string | null) => {
    setSelectedObjective(objectiveId);
    if (objectiveId) {
      setSelectedStage(indexes.objectivesById.get(objectiveId)?.stage ?? null);
    }
  };
  const handleSelectEnabler = (code: string | null) => {
    setSelectedEnabler(code);
    if (code) {
      setSelectedEnablerTheme(indexes.enablersByCode.get(code)?.theme ?? null);
    }
  };

  if (!isHomeRoute) {
    return <NotFound />;
  }

  return (
    <div className="site-shell">
      <header className="hero">
        <div className="container hero__inner">
          <img
            src="/assets/navigator-logo.png"
            alt="Navigator"
            className="hero__logo"
          />
          <h1>Learning Materials</h1>
          <p>
            Hand-picked videos, articles, books and podcasts, organised by startup key objectives and execution enablers.
          </p>
          <SearchBox value={searchTerm} onChange={setSearchTerm} />
        </div>
      </header>

      <main className="container content">
        {isLoading ? <LoadingState /> : null}
        {error ? <ErrorState /> : null}
        {!isLoading && !error ? (
          <>
            <div className="browse-toggle" role="tablist" aria-label="Browse mode">
              <button
                className={browseMode === "objectives" ? "browse-toggle__button browse-toggle__button--active" : "browse-toggle__button"}
                type="button"
                role="tab"
                aria-selected={browseMode === "objectives"}
                onClick={() => setBrowseMode("objectives")}
              >
                Key Objectives
              </button>
              <button
                className={browseMode === "enablers" ? "browse-toggle__button browse-toggle__button--active" : "browse-toggle__button"}
                type="button"
                role="tab"
                aria-selected={browseMode === "enablers"}
                onClick={() => setBrowseMode("enablers")}
              >
                Enablers
              </button>
            </div>

            <div className="filters">
              <FilterPills
                label="Category"
                options={RESOURCE_CATEGORIES}
                selected={selectedCategory}
                onSelect={(category) => {
                  if (category) {
                    setSelectedCategory(category as ResourceCategory);
                  }
                }}
                includeAllOption={false}
              />
              {browseMode === "objectives" ? (
                <ObjectiveFilter
                  objectives={catalog.objectives}
                  selectedStage={selectedStage}
                  selectedObjective={selectedObjective}
                  onSelectStage={handleSelectStage}
                  onSelectObjective={handleSelectObjective}
                />
              ) : (
                <EnablerFilter
                  enablers={catalog.enablers}
                  selectedTheme={selectedEnablerTheme}
                  selectedEnabler={selectedEnabler}
                  onSelectTheme={setSelectedEnablerTheme}
                  onSelectEnabler={handleSelectEnabler}
                />
              )}
              {selectedCategory === DEFAULT_RESOURCE_CATEGORY ? (
                <FilterPills label="Format" options={formats} selected={selectedFormat} onSelect={setSelectedFormat} />
              ) : null}
            </div>

            <div className="result-bar">
              <p>
                <strong>{activeGroups.length}</strong> {browseMode === "objectives" ? "key objective" : "enabler"}
                {activeGroups.length === 1 ? "" : "s"} / <strong>{filteredResourceCount}</strong> resource
                {filteredResourceCount === 1 ? "" : "s"}
              </p>
              {hasActiveFilters ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedCategory(DEFAULT_RESOURCE_CATEGORY);
                    setSelectedStage(null);
                    setSelectedObjective(null);
                    setSelectedFormat(null);
                    setSelectedEnablerTheme(null);
                    setSelectedEnabler(null);
                  }}
                >
                  Clear all
                </button>
              ) : null}
            </div>

            {activeGroups.length === 0 ? (
              <EmptyState />
            ) : browseMode === "objectives" ? (
              <section className="objective-list" aria-label="Learning key objectives">
                {objectiveGroups.map((objective) => (
                  <ObjectiveCard
                    key={objective.id}
                    objective={objective}
                    enablersByCode={indexes.enablersByCode}
                    defaultOpen={shouldOpenMatches}
                    searchTerm={searchTerm}
                  />
                ))}
              </section>
            ) : (
              <section className="objective-list" aria-label="Execution enablers">
                {enablerGroups.map((enabler) => (
                  <EnablerCard
                    key={enabler.code}
                    enabler={enabler}
                    objectivesById={indexes.objectivesById}
                    defaultOpen={shouldOpenMatches}
                    searchTerm={searchTerm}
                  />
                ))}
              </section>
            )}
          </>
        ) : null}
      </main>

      <footer className="site-footer">
        <div className="container site-footer__inner">
          <span>Go-Together Learning Materials &amp; Enablers</span>
        </div>
      </footer>
      {!isLoading && !error ? <ChatWidget catalog={catalog} /> : null}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="state state--loading" role="status">
      <span className="spinner" />
      Loading the library...
    </div>
  );
}

function ErrorState() {
  return (
    <div className="state state--error" role="alert">
      Failed to load the library. Please refresh.
    </div>
  );
}

function EmptyState() {
  return (
    <div className="state state--empty">
      <h2>Nothing matches yet.</h2>
      <p>Try a different search term or clear your filters.</p>
    </div>
  );
}

function NotFound() {
  return (
    <div className="not-found">
      <div>
        <p className="not-found__code">404</p>
        <h1>Page Not Found</h1>
        <p>The page "{window.location.pathname.slice(1)}" could not be found in this site.</p>
        <a href="/">Go Home</a>
      </div>
    </div>
  );
}
